import type { EventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import type { PositionedEvent } from '@repo/types/calendar';
import type { MeetingPublic } from '@repo/types/meetings';

import { useCurrentTimeIndicator } from '@repo/hooks/useCurrentTimeIndicator';
import { cn } from '@repo/lib/cn';
import { CalendarViewCellHeight } from '@repo/lib/constants/calendar-constants';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { VIEW_KEY } from '@repo/types/enums/viewKey';
import { addHours, areIntervalsOverlapping, differenceInMinutes, eachDayOfInterval, eachHourOfInterval, format, getHours, getMinutes, isBefore, isSameDay, isToday, startOfDay } from 'date-fns';
import { useEffect, useMemo } from 'react';

import { DraggableEvent, DroppableCell, EventItem } from '@/components/features/calendar';
import { Button } from '@/components/ui/button';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useCredentials } from '@/store/auth-store';
import { getCalendarEventUniqueId, type CalendarEvent, isHolidayCalendarEvent, isMultiDayEvent } from '@/utils/calendar';

import { useCalendarEvents } from './useCalendarEvents';

interface WeekViewProps {
  rangeDates: EventCalendarRangeDates;
  currentDate: Date;
  onEventSelect: (event: MeetingPublic) => void;
  onEventCreate: (event: Date) => void;
}

export function WeekView(weekViewProps: WeekViewProps) {
  const { rangeDates, currentDate, onEventSelect, onEventCreate } = weekViewProps;

  const { lvl } = useCredentials();
  const lvlDisabled = useMemo<boolean>(() => lvl <= 0, [lvl]);

  const days = useMemo(() => {
    return eachDayOfInterval({
      start: new Date(rangeDates.after),
      end: new Date(rangeDates.before),
    });
  }, [rangeDates]);

  const hours = useMemo(() => {
    const dayStart = startOfDay(currentDate);
    return eachHourOfInterval({
      start: dayStart,
      end: addHours(dayStart, 23),
    });
  }, [currentDate]);

  const { events, isError, error, refetch } = useCalendarEvents(rangeDates);

  // Get all-day events and multi-day events for the week
  const allDayEvents = useMemo(() => {
    return events
      .filter((event) => {
        // Include explicitly marked all-day events or multi-day events
        return event.allDay || isMultiDayEvent(event);
      })
      .filter((event) => {
        const eventStart = new Date(event.startDate);
        const eventEnd = new Date(event.endDate);
        return days.some((day) => isSameDay(day, eventStart) || isSameDay(day, eventEnd) || (day > eventStart && day < eventEnd));
      });
  }, [events, days]);

  // Process events for each day to calculate positions
  const processedDayEvents = useMemo(() => {
    const result = days.map((day) => {
      // Get events for this day that are not all-day events or multi-day events
      const dayEvents = events.filter((event) => {
        // Skip all-day events and multi-day events
        if (event.allDay || isMultiDayEvent(event)) return false;

        const eventStart = new Date(event.startDate);
        const eventEnd = new Date(event.endDate);

        // Check if event is on this day
        return isSameDay(day, eventStart) || isSameDay(day, eventEnd) || (eventStart < day && eventEnd > day);
      });

      // Sort events by start time and duration
      const sortedEvents = [...dayEvents].sort((a, b) => {
        const aStart = new Date(a.startDate);
        const bStart = new Date(b.startDate);
        const aEnd = new Date(a.endDate);
        const bEnd = new Date(b.endDate);

        // First sort by start time
        if (aStart < bStart) return -1;
        if (aStart > bStart) return 1;

        // If start times are equal, sort by duration (longer events first)
        const aDuration = differenceInMinutes(aEnd, aStart);
        const bDuration = differenceInMinutes(bEnd, bStart);
        return bDuration - aDuration;
      });

      // Calculate positions for each event
      const positionedEvents: PositionedEvent<CalendarEvent>[] = [];
      const dayStart = startOfDay(day);

      // Track columns for overlapping events
      const columns: { event: CalendarEvent; end: Date }[][] = [];

      sortedEvents.forEach((event) => {
        const eventStart = new Date(event.startDate);
        const eventEnd = new Date(event.endDate);

        // Adjust start and end times if they're outside this day
        const adjustedStart = isSameDay(day, eventStart) ? eventStart : dayStart;
        const adjustedEnd = isSameDay(day, eventEnd) ? eventEnd : addHours(dayStart, 24);

        // Calculate top position and height
        const startHour = getHours(adjustedStart) + getMinutes(adjustedStart) / 60;
        const endHour = getHours(adjustedEnd) + getMinutes(adjustedEnd) / 60;
        const top = startHour * CalendarViewCellHeight;
        const height = (endHour - startHour) * CalendarViewCellHeight;

        // Find a column for this event
        let columnIndex = 0;
        let placed = false;

        while (!placed) {
          if (!columns[columnIndex]) {
            columns[columnIndex] = [];
            placed = true;
          } else {
            // Check if this event overlaps with any event in this column
            const overlaps = columns[columnIndex]?.some((col) =>
              areIntervalsOverlapping(
                { start: adjustedStart, end: adjustedEnd },
                {
                  start: new Date(col.event.startDate),
                  end: new Date(col.event.endDate),
                }
              )
            );

            if (!overlaps) {
              placed = true;
            } else {
              columnIndex++;
            }
          }
        }

        // Add event to its column
        columns[columnIndex]?.push({ event, end: adjustedEnd });

        // Calculate width and left position based on number of columns
        const width = columnIndex === 0 ? 1 : Math.max(1 - columnIndex * 0.1, 0.4);
        const left = columnIndex === 0 ? 0 : columnIndex * 0.1;

        positionedEvents.push({
          event,
          top,
          height,
          left,
          width,
          zIndex: 10 + columnIndex, // Higher columns get higher z-index
        });
      });

      return positionedEvents;
    });

    return result;
  }, [days, events]);

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isHolidayCalendarEvent(event)) return;
    onEventSelect(event);
  };

  const showAllDaySection = allDayEvents.length > 0;
  const { currentTimePosition, currentTimeVisible } = useCurrentTimeIndicator(currentDate, VIEW_KEY.WEEK);

  useEffect(() => {
    if (isError) {
      toasterMessageHandler(baseErrorMessages, error, {
        action: (
          <Button
            size={'sm'}
            className={'ml-auto'}
            variant={'tertiary'}
            onClick={() => {
              void refetch();
            }}
          >
            Ponów
          </Button>
        ),
      });
    }
  }, [error, isError, refetch]);

  return (
    <div className="flex h-full flex-col">
      <div className="md:bg-background bg-background-dark divide-border-primary/70 sticky top-(--calendar-header-height) z-20 grid grid-cols-8 divide-x border-y">
        <div className="text-muted-foreground/70 py-2 text-center text-sm">
          <span className="max-sm:sr-only">{format(new Date(), 'O')}</span>
        </div>
        {days.map((day) => (
          <div key={day.toString()} className="data-today:text-brand text-muted-foreground py-2 text-center text-sm data-today:font-medium" data-today={isToday(day) || undefined}>
            <span className="sm:hidden" aria-hidden="true">
              {format(day, 'E')[0]} {format(day, 'd')}
            </span>
            <span className="max-sm:hidden">{format(day, 'EEE dd')}</span>
          </div>
        ))}
      </div>

      {showAllDaySection && (
        <div className="border-border-primary/70 bg-accent dark:bg-card border-b">
          <div className="grid grid-cols-8">
            <div className="border-border-primary/70 relative border-r">
              <span className="text-muted-foreground absolute bottom-0 left-0 h-6 w-16 max-w-full pe-2 text-right text-[10px] sm:pe-4 sm:text-xs">C. d.</span>
            </div>
            {days.map((day, dayIndex) => {
              const dayAllDayEvents = allDayEvents.filter((event) => {
                const eventStart = new Date(event.startDate);
                const eventEnd = new Date(event.endDate);
                return isSameDay(day, eventStart) || (day > eventStart && day < eventEnd) || isSameDay(day, eventEnd);
              });

              return (
                <div key={day.toString()} className="border-border-primary/70 relative border-r p-1 last:border-r-0" data-today={isToday(day) || undefined}>
                  {dayAllDayEvents.map((event) => {
                    const eventStart = new Date(event.startDate);
                    const eventEnd = new Date(event.endDate);
                    const isFirstDay = isSameDay(day, eventStart);
                    const isLastDay = isSameDay(day, eventEnd);

                    // Check if this is the first day in the current week view
                    const isFirstVisibleDay = dayIndex === 0 && isBefore(eventStart, new Date(rangeDates.after));
                    const shouldShowTitle = isFirstDay || isFirstVisibleDay;

                    return (
                      <EventItem
                        key={`spanning-${getCalendarEventUniqueId(event)}`}
                        onClick={(e) => handleEventClick(event, e)}
                        event={event}
                        view={VIEW_KEY.MONTH}
                        isFirstDay={isFirstDay}
                        isLastDay={isLastDay}
                      >
                        {/* Show title if it's the first day of the event or the first visible day in the week */}
                        <div className={cn('truncate', !shouldShowTitle && 'invisible')} aria-hidden={!shouldShowTitle}>
                          {event.name}
                        </div>
                      </EventItem>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid flex-1 grid-cols-8">
        <div className="border-border-primary/70 border-r">
          {hours.map((hour, index) => (
            <div key={hour.toString()} className="border-border-primary/70 relative h-(--week-cells-height) border-b last:border-b-0">
              {index > 0 && (
                <span className="bg-background-dark md:bg-background text-muted-foreground absolute -top-3 left-0 flex h-6 w-16 max-w-full items-center justify-end pe-2 text-[10px] sm:pe-4 sm:text-xs">
                  {format(hour, 'HH:mm')}
                </span>
              )}
            </div>
          ))}
        </div>

        {days.map((day, dayIndex) => (
          <div key={day.toString()} className="border-border-primary/70 relative border-r last:border-r-0" data-today={isToday(day) || undefined}>
            {/* Positioned events */}
            {processedDayEvents[dayIndex]?.map((positionedEvent) => (
              <div
                key={getCalendarEventUniqueId(positionedEvent.event)}
                className="absolute z-10 px-0.5"
                style={{
                  top: `${positionedEvent.top}px`,
                  height: `${positionedEvent.height}px`,
                  left: `${positionedEvent.left * 100}%`,
                  width: `${positionedEvent.width * 100}%`,
                  zIndex: positionedEvent.zIndex,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="h-full w-full">
                  <DraggableEvent
                    event={positionedEvent.event}
                    view={VIEW_KEY.WEEK}
                    disabled={lvlDisabled || isHolidayCalendarEvent(positionedEvent.event)}
                    onClick={(e) => handleEventClick(positionedEvent.event, e)}
                    showTime
                    height={positionedEvent.height}
                  />
                </div>
              </div>
            ))}

            {/* Current time indicator - only show for today's column */}
            {currentTimeVisible && isToday(day) && (
              <div className="pointer-events-none absolute right-0 left-0 z-20" style={{ top: `${currentTimePosition}%` }}>
                <div className="relative flex items-center">
                  <div className="bg-brand absolute -left-1 h-2 w-2 rounded-full"></div>
                  <div className="bg-brand h-[2px] w-full"></div>
                </div>
              </div>
            )}
            {hours.map((hour) => {
              const hourValue = getHours(hour);
              return (
                <div key={hour.toString()} className="border-border-primary/70 relative h-(--week-cells-height) border-b last:border-b-0">
                  {/* Quarter-hour intervals */}
                  {[0, 1, 2, 3].map((quarter) => {
                    const quarterHourTime = hourValue + quarter * 0.25;
                    return (
                      <DroppableCell
                        key={`${hour.toString()}-${quarter}`}
                        id={`week-cell-${day.toISOString()}-${quarterHourTime}`}
                        date={day}
                        disabled={lvlDisabled}
                        time={quarterHourTime}
                        className={cn(
                          'absolute h-[calc(var(--week-cells-height)/4)] w-full',
                          quarter === 0 && 'top-0',
                          quarter === 1 && 'top-[calc(var(--week-cells-height)/4)]',
                          quarter === 2 && 'top-[calc(var(--week-cells-height)/4*2)]',
                          quarter === 3 && 'top-[calc(var(--week-cells-height)/4*3)]'
                        )}
                        onClick={() => {
                          const startTime = new Date(day);
                          startTime.setHours(hourValue);
                          startTime.setMinutes(quarter * 15);
                          startTime.setSeconds(0);
                          startTime.setMilliseconds(0);
                          onEventCreate(startTime);
                        }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
